import React, { useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { ArcElement, Chart as ChartJS, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import classes from '../../bidAnalysisTemplate.module.scss';

ChartJS.register(ArcElement, Tooltip, Legend);

interface VolumeByCotItem {
  cot: string;
  volume: number;
}

interface VolumeByCotChartProps {
  data: VolumeByCotItem[];
  isLoading?: boolean;
  selectedCompany?: string | null;
}

const SLICE_COLORS = [
  '#522d6e',
  '#7c3aed',
  '#a78bfa',
  '#10b981',
  '#34d399',
  '#f59e0b',
  '#fb923c',
  '#60a5fa',
  '#f472b6',
  '#6ee7b7',
  '#fbbf24',
  '#818cf8',
];

export const VolumeByCotChart: React.FC<VolumeByCotChartProps> = ({
  data,
  isLoading = false,
  selectedCompany,
}) => {
  const lastDataRef = useRef<VolumeByCotItem[]>([]);

  const displayData = useMemo(() => {
    if (data && data.length > 0) {
      lastDataRef.current = data;
      return data;
    }
    return lastDataRef.current;
  }, [data]);

  const totalVolume = useMemo(
    () => displayData.reduce((sum, d) => sum + (d.volume || 0), 0),
    [displayData]
  );

  const chartData = useMemo(() => ({
    labels: displayData.map(d => d.cot),
    datasets: [
      {
        data: displayData.map(d => d.volume),
        backgroundColor: displayData.map((_, i) => SLICE_COLORS[i % SLICE_COLORS.length]),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 10,
      },
    ],
  }), [displayData]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 8, right: 24, bottom: 8, left: 24 } },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'center',
        labels: {
          boxWidth: 12,
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 13, color: '#374151' },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.parsed as number;
            const pct = totalVolume > 0 ? ((val / totalVolume) * 100).toFixed(2) : '0.00';
            return ` ${ctx.label}: ${val.toLocaleString()} (${pct}%)`;
          },
        },
      },
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'start',
        offset: 8,
        clamp: true,
        formatter: (_value: number, ctx: any) => {
          const dataArr = ctx.dataset?.data ?? [];
          const total = dataArr.reduce(
            (a: number, b: number) => (Number(a) || 0) + (Number(b) || 0),
            0
          );
          const idx = ctx.dataIndex ?? 0;
          const raw = Number(dataArr[idx] ?? ctx.parsed ?? ctx.raw ?? 0);
          const pct =
            total > 0 && Number.isFinite(raw)
              ? ((raw / total) * 100).toFixed(2)
              : '0.00';
          return `${pct}%`;
        },
        font: { size: 14, color: '#ffffff', weight: '600' },
        color: '#ffffff',
        backgroundColor: 'transparent',
        borderWidth: 0,
      },
    },
  }), [totalVolume]);

  return (
    <div className={classes.chartBox} style={{ minHeight: 'unset' }}>
      {/* Header — matches MarketTrendsChart style */}
      <div className={classes.chartBoxHeader}>
        <h4>
          Volume by Class of Trade
          {selectedCompany && (
            <span className={classes.companyName}> - {selectedCompany}</span>
          )}
        </h4>
      </div>

      {/* Subtitle */}
      <div className={classes.chartSubtitle}>
        <span>
          {selectedCompany
            ? `Showing COT distribution for ${selectedCompany}`
            : 'Distribution of volume across trade classes'}
        </span>
      </div>

      {/* Body */}
      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2.5rem', color: '#522d6e' }}>
          <div style={{ textAlign: 'center' }}>
            <i className="pi pi-spinner pi-spin" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }} />
            <span style={{ fontSize: '0.875rem' }}>Loading volume data...</span>
          </div>
        </div>
      ) : displayData.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2.5rem', textAlign: 'center', color: '#6b7280' }}>
          <div>
            <i className="pi pi-chart-pie" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }} />
            <span style={{ fontSize: '0.875rem' }}>No volume data available. Please validate NDC to load chart data.</span>
          </div>
        </div>
      ) : (
        <div style={{ padding: '0.5rem 0', minHeight: '420px', width: '100%' }}>
          <Chart
            type="pie"
            data={chartData}
            options={chartOptions as any}
            plugins={[ChartDataLabels]}
          />
        </div>
      )}
    </div>
  );
};
